<script lang="ts" setup>
import type { HeaderNavigationModel } from '@su4r-frontend/api';

const { data } = defineProps<{ data?: HeaderNavigationModel }>();
const { data: authData } = useAuth();

const { locales, locale, t } = useI18n();
const localeRoute = useLocaleRoute();
const localePath = useLocalePath();
const { status, signOut } = useAuth();
const switchLocalePath = useSwitchLocalePath();

const openSubMenu = ref('');
const languageToggleIsOpen = ref<boolean>(false);

const availableLocales = computed(() => {
    return locales.value.filter((i) => i.code !== locale.value);
});

const toggleLanguageMenu = (): void => {
    closeMenu();
    languageToggleIsOpen.value = !languageToggleIsOpen.value;
};

const toggleNav = (key: string): void => {
    openSubMenu.value = openSubMenu.value === key ? '' : key;
};

const closeMenu = (): void => {
    openSubMenu.value = '';
};

const dashboardUrl = computed(() => {
    if (!authData.value) return;
    if (authData.value.user.role === 'practitioner') {
        return localePath('/dashboard');
    }
    return localePath(`/dashboard/${authData.value.user.name}/current`);
});

onMounted(() => {
    document.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            closeMenu();
        }
    });
});

onUnmounted(() => {
    document.removeEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            closeMenu();
        }
    });
});
</script>

<template>
    <header class="z-80 container py-8 text-white">
        <a href="#main-content" class="skip-to invisible absolute -left-full">{{
            $t('common.skipToContent')
        }}</a>
        <nav class="flex items-center justify-between">
            <ul class="flex gap-4">
                <li class="self-center">
                    <NuxtLink
                        :aria-label="t('common.home')"
                        :to="localeRoute('/')"
                        class="cursor-pointer">
                        <SvgComponent class="h-8 w-[129px]" icon="logo-uptimise" />
                    </NuxtLink>
                </li>
                <li v-for="link in data?.navigationItems" :key="JSON.stringify(link)">
                    <button
                        v-if="isBaseNavigationLinkGroup(link)"
                        class="navigation-link relative"
                        :class="{ active: openSubMenu === link.title }"
                        :aria-expanded="openSubMenu === link.title"
                        :aria-controls="removeWhiteSpaceAndSpecialCharacters(link.title)"
                        @click="toggleNav(link.title ?? '/')">
                        {{ link.title }}
                        <SvgComponent
                            class="h-4 w-4 transition-transform"
                            :class="{ 'rotate-180': openSubMenu === link.title }"
                            icon="chevron-down" />
                        <div
                            :id="removeWhiteSpaceAndSpecialCharacters(link.title)"
                            class="submenu-container"
                            :class="{ open: openSubMenu === link.title }"
                            role="region">
                            <ul class="text-blue-heavy w-max overflow-hidden rounded bg-white">
                                <li
                                    v-for="item in link.items"
                                    :key="item.title"
                                    class="overflow-hidden">
                                    <NuxtLink
                                        :to="localeRoute(item.url)"
                                        :target="item.openInNewTab ? '_blank' : '_self'"
                                        class="dropdown-link"
                                        :tabindex="openSubMenu === link.title ? 0 : -1">
                                        {{ item.title }}
                                    </NuxtLink>
                                </li>
                            </ul>
                        </div>
                    </button>
                    <div v-else>
                        <NuxtLink
                            class="navigation-link"
                            :to="localeRoute(link.url ?? '/')"
                            :target="link.openInNewTab ? '_blank' : '_self'">
                            {{ link.title }}
                        </NuxtLink>
                    </div>
                </li>
            </ul>

            <ul class="flex items-center gap-4">
                <li>
                    <button
                        class="navigation-link relative"
                        :class="{ active: languageToggleIsOpen }"
                        :aria-expanded="languageToggleIsOpen"
                        aria-controls="language"
                        @click="toggleLanguageMenu">
                        {{ locale.toUpperCase() }}
                        <SvgComponent
                            class="h-4 w-4 transition-transform"
                            :class="{ 'rotate-180': languageToggleIsOpen }"
                            icon="chevron-down" />
                        <div
                            id="language"
                            class="submenu-container"
                            :class="{ open: languageToggleIsOpen }"
                            role="region">
                            <ul class="text-blue-heavy w-max overflow-hidden rounded bg-white">
                                <li
                                    v-for="loc in availableLocales"
                                    :key="loc.code"
                                    class="overflow-hidden">
                                    <NuxtLink
                                        class="dropdown-link"
                                        :to="switchLocalePath(loc.code)">
                                        {{ loc.code.toUpperCase() }}
                                    </NuxtLink>
                                </li>
                            </ul>
                        </div>
                    </button>
                </li>

                <template v-if="status === 'authenticated'">
                    <li>
                        <NuxtLink class="btn btn-secondary-b" :to="dashboardUrl">{{
                            $t('common.dashboard')
                        }}</NuxtLink>
                    </li>
                    <li>
                        <button
                            class="btn btn-primary-b"
                            type="button"
                            @click="() => signOut({ callbackUrl: `/${locale}/` })">
                            {{ $t('common.logout') }}
                        </button>
                    </li>
                </template>
                <li v-else>
                    <NuxtLink
                        v-if="data?.login"
                        class="btn btn-primary-b"
                        :target="data.login.openInNewTab ? '_black' : undefined"
                        :to="localeRoute(data.login.url)"
                        >{{ data.login.title }}</NuxtLink
                    >
                </li>
            </ul>
        </nav>
    </header>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.navigation-link {
    @apply ring-blue-medium flex cursor-pointer items-center gap-1 rounded px-4 py-3.5 leading-4 hover:ring;

    &.active {
        @apply ring;
    }
}

.submenu-container {
    @apply absolute top-[calc(100%+8px)] left-0 z-100 grid grid-rows-[0fr] overflow-hidden shadow transition-[grid-template-rows] duration-300;

    &.open {
        @apply grid-rows-[1fr];
    }
}

.dropdown-link {
    @apply btn cursor-pointer justify-start overflow-hidden rounded-none text-left whitespace-nowrap lg:max-w-full;

    &:focus,
    &:hover {
        @apply bg-blue-medium text-black;
    }
}
</style>
